import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import BarChartOutlinedIcon from '@material-ui/icons/BarChartOutlined';
import CloseIcon from '@material-ui/icons/Close';
import FilterListRoundedIcon from '@material-ui/icons/FilterListRounded';
import TopNav from '../common/TopNav';
import Container from '../Elements/Container';
import ConsumptionHistoryChart from './ConsumptionHistoryChart';
import { getProviders } from '../../services/billing';
import { getConsumptionHistory } from '../../services/billing-v2';

const GRANULARITY_OPTIONS = [
    { value: 'mensual', label: 'Mensual' },
    { value: 'anual', label: 'Anual' },
];

const SERVICE_OPTIONS = [
    { value: '', label: 'Todos los servicios' },
    { value: 'electricidad', label: 'Energia electrica' },
    { value: 'agua', label: 'Agua' },
    { value: 'gas', label: 'Gas' },
];

const GROUP_COLORS = [
    '#76B5F3',
    '#4995EA',
    '#2577CB',
    '#6FC9E9',
    '#3087C8',
    '#90B7F8',
    '#54A5DD',
    '#7FB2E6',
];

const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.palette.surface.panel} 0%, ${theme.palette.background.default} 100%)`,
        minHeight: 'calc(100vh - 112px)',
    },
    hero: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2.5),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.main}`,
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.surface.soft} 100%)`,
        boxShadow: theme.palette.shadow.card,
    },
    heroRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
        '@media (max-width: 960px)': {
            flexDirection: 'column',
        },
    },
    titleWrap: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
    },
    titleIcon: {
        width: 52,
        height: 52,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.accentSoft,
    },
    pageTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    pageSubtitle: {
        color: theme.palette.text.secondary,
        maxWidth: 760,
        lineHeight: 1.55,
        marginTop: theme.spacing(0.5),
    },
    helperText: {
        marginTop: theme.spacing(1.25),
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    headerActions: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    primaryButton: {
        backgroundColor: theme.palette.surface.primary,
        color: theme.palette.primary.main,
        border: `1px solid ${theme.palette.surface.accentBorder}`,
        textTransform: "uppercase",
        boxShadow: "none",
        transition: theme.transitions.create(["background-color", "border-color", "box-shadow", "color"], {
            duration: theme.transitions.duration.short,
        }),
        "&:hover": {
            backgroundColor: theme.palette.surface.selected,
            borderColor: theme.palette.primary.dark,
            color: theme.palette.primary.dark,
            boxShadow: "none",
        },
    },
    secondaryButton: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        padding: theme.spacing(1, 2),
        textTransform: 'none',
        fontWeight: 700,
        backgroundColor: theme.palette.surface.alt,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    card: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
    },
    cardHeader: {
        padding: theme.spacing(1.75, 2),
        backgroundColor: theme.palette.surface.soft,
        borderBottom: `1px solid ${theme.palette.border.soft}`,
    },
    sectionTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    sectionCaption: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.4),
    },
    summaryGrid: {
        marginBottom: theme.spacing(2),
    },
    summaryCard: {
        padding: theme.spacing(2),
        height: '100%',
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.surface.soft} 100%)`,
        boxShadow: theme.palette.shadow.card,
    },
    summaryLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: theme.palette.text.disabled,
        marginBottom: theme.spacing(0.8),
    },
    summaryValue: {
        fontSize: 24,
        fontWeight: 700,
        color: theme.palette.grey.main,
        lineHeight: 1.1,
    },
    summaryHint: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    filtersCard: {
        height: '100%',
    },
    filtersBody: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    filterDrawerPaper: {
        width: 420,
        maxWidth: '100vw',
        background: `linear-gradient(180deg, ${theme.palette.surface.soft} 0%, ${theme.palette.background.default} 100%)`,
        borderLeft: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.dialog,
        '@media (max-width: 640px)': {
            width: '100vw',
        },
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 2, 1.5),
        borderBottom: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
    },
    drawerHeaderText: {
        minWidth: 0,
    },
    drawerTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    drawerSubtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.35),
        lineHeight: 1.5,
    },
    drawerContent: {
        padding: theme.spacing(2),
        height: '100%',
        overflowY: 'auto',
    },
    field: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiInputLabel-outlined': {
            fontSize: 13,
        },
    },
    segmentedControl: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
    },
    segmentButton: {
        minHeight: 42,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.button}`,
        backgroundColor: theme.palette.surface.alt,
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    segmentButtonActive: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.selected,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    filterActions: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
    },
    mainColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    chartCardBody: {
        padding: theme.spacing(2),
    },
    chartShell: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.soft}`,
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.surface.soft} 100%)`,
        padding: theme.spacing(2),
    },
    tableCardBody: {
        padding: theme.spacing(0, 0, 1),
    },
    tableHead: {
        backgroundColor: theme.palette.primary.main,
    },
    headCell: {
        color: theme.palette.common.white,
        fontWeight: 700,
        borderBottom: 'none',
        whiteSpace: 'nowrap',
    },
    bodyCell: {
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        whiteSpace: 'nowrap',
    },
    stickyFirstCell: {
        position: 'sticky',
        left: 0,
        zIndex: 2,
        backgroundColor: theme.palette.background.default,
    },
    stickyFirstHeadCell: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: theme.palette.primary.main,
    },
    inlineAlert: {
        marginBottom: theme.spacing(2),
    },
    chipsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    chip: {
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.button}`,
        backgroundColor: theme.palette.background.default,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.surface.selected,
            borderColor: theme.palette.primary.main,
        },
    },
    metricToggle: {
        display: 'flex',
        gap: 4,
        flexShrink: 0,
    },
    metricToggleBtn: {
        minHeight: 28,
        padding: theme.spacing(0.25, 1.25),
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        border: `1px solid ${theme.palette.border.button}`,
        backgroundColor: theme.palette.surface.alt,
        color: theme.palette.text.secondary,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    metricToggleBtnActive: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.selected,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: theme.palette.surface.selected,
        },
    },
    emptyState: {
        padding: theme.spacing(5, 3),
        textAlign: 'center',
        border: `1px dashed ${theme.palette.border.main}`,
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        backgroundColor: theme.palette.surface.soft,
    },
    emptyStateTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        marginBottom: theme.spacing(0.75),
    },
    emptyStateText: {
        color: theme.palette.text.secondary,
        maxWidth: 560,
        margin: '0 auto',
        lineHeight: 1.6,
    },
    loadingBox: {
        minHeight: 360,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const normalizeProvider = provider => ({
    id: String(provider?.id ?? provider?.provider_id ?? ''),
    name: provider?.name ?? provider?.nombre ?? provider?.provider_name ?? '',
});

const buildDefaultPeriod = (granularity, referenceDate = new Date()) => {
    const year = referenceDate.getFullYear();
    const month = String(referenceDate.getMonth() + 1).padStart(2, '0');

    if (granularity === 'anual') {
        return {
            from: String(year - 4),
            to: String(year),
        };
    }

    return {
        from: `${year}-01`,
        to: `${year}-${month}`,
    };
};

const buildInitialFilters = () => {
    const defaultPeriod = buildDefaultPeriod('mensual');

    return {
        granularity: 'mensual',
        periodFrom: defaultPeriod.from,
        periodTo: defaultPeriod.to,
        serviceType: 'electricidad',
        providerId: '',
        siteIds: [],
    };
};

const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
});

const integerFormatter = new Intl.NumberFormat('es-AR');

const formatCurrency = value => currencyFormatter.format(Number(value) || 0);

const formatNumber = (value, fractionDigits = 1) =>
    new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    }).format(Number(value) || 0);

const buildDateRange = filters => {
    const { granularity, periodFrom, periodTo } = filters;

    if (granularity === 'anual') {
        return {
            fecha_desde: `${periodFrom}-01-01`,
            fecha_hasta: `${periodTo}-12-31`,
        };
    }

    if (granularity === 'mensual') {
        const [fromYear, fromMonth] = (periodFrom || '').split('-');
        const [toYear, toMonth] = (periodTo || '').split('-');
        const parsedToYear = Number(toYear);
        const parsedToMonth = Number(toMonth);
        const lastDay = parsedToYear && parsedToMonth
            ? new Date(parsedToYear, parsedToMonth, 0).getDate()
            : null;

        return {
            fecha_desde: `${fromYear}-${fromMonth}-01`,
            fecha_hasta: `${toYear}-${toMonth}-${String(lastDay || '').padStart(2, '0')}`,
            isValid: Boolean(fromYear && fromMonth && toYear && toMonth && lastDay),
        };
    }

    return {
        fecha_desde: periodFrom,
        fecha_hasta: periodTo,
    };
};

const comparePeriodStrings = (firstValue, secondValue) => firstValue.localeCompare(secondValue);

const generatePeriodRange = (from, to, granularity) => {
    const periods = [];

    if (granularity === 'anual') {
        const fromYear = parseInt(from, 10);
        const toYear = parseInt(to, 10);
        if (!isNaN(fromYear) && !isNaN(toYear) && fromYear <= toYear) {
            for (let year = fromYear; year <= toYear; year++) {
                periods.push(String(year));
            }
        }
    } else {
        const [fromYear, fromMonth] = (from || '').split('-').map(Number);
        const [toYear, toMonth] = (to || '').split('-').map(Number);
        if (fromYear && fromMonth && toYear && toMonth) {
            let y = fromYear;
            let m = fromMonth;
            while (y < toYear || (y === toYear && m <= toMonth)) {
                periods.push(`${y}-${String(m).padStart(2, '0')}`);
                m++;
                if (m > 12) { m = 1; y++; }
            }
        }
    }

    return periods;
};

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const getPeriodLabel = (period, granularity) => {
    if (!period) {
        return '-';
    }

    if (granularity === 'anual') {
        return period;
    }

    const [year, month] = period.split('-');
    const monthName = MONTH_NAMES[parseInt(month, 10) - 1] ?? month;
    return `${monthName}. ${year}`;
};

function ConsumptionHistory() {
    const classes = useStyles();
    const [filters, setFilters] = useState(buildInitialFilters);
    const [appliedFilters, setAppliedFilters] = useState(buildInitialFilters);
    const [providers, setProviders] = useState([]);
    const [loadingMetadata, setLoadingMetadata] = useState(true);
    const [loadingData, setLoadingData] = useState(true);
    const [pageError, setPageError] = useState('');
    const [series, setSeries] = useState([]);
    const [gaps, setGaps] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [chartMetric, setChartMetric] = useState('cost');

    useEffect(() => {
        const loadMetadata = async () => {
            setLoadingMetadata(true);

            try {
                const providersResponse = await getProviders();

                const providerItems = Array.isArray(providersResponse?.data)
                    ? providersResponse.data.map(normalizeProvider).filter(item => item.id && item.name)
                    : [];

                setProviders(providerItems);
            }
            catch (error) {
                setProviders([]);
                setPageError('No pudimos cargar los filtros de proveedores.');
            }
            finally {
                setLoadingMetadata(false);
            }
        };

        loadMetadata();
    }, []);

    useEffect(() => {
        const loadConsumptions = async () => {
            setLoadingData(true);
            setPageError('');

            const { fecha_desde, fecha_hasta, isValid } = buildDateRange(appliedFilters);

            if (isValid === false || !fecha_desde || !fecha_hasta) {
                setSeries([]);
                setGaps([]);
                setPageError('Completá un rango de fechas válido para consultar los consumos.');
                setLoadingData(false);
                return;
            }

            try {
                const response = await getConsumptionHistory({
                    fecha_desde,
                    fecha_hasta,
                    granularidad: appliedFilters.granularity,
                    tipo_servicio: appliedFilters.serviceType,
                    jerarquia_id: appliedFilters.siteIds,
                    proveedor_id: appliedFilters.providerId,
                });

                setSeries(Array.isArray(response?.data?.series) ? response.data.series : []);
                setGaps(Array.isArray(response?.data?.gaps) ? response.data.gaps : []);
            } catch (error) {
                setSeries([]);
                setGaps([]);
                setPageError('No pudimos cargar los consumos. Intentá de nuevo.');
            } finally {
                setLoadingData(false);
            }
        };

        loadConsumptions();
    }, [appliedFilters]);

    const groupedData = useMemo(() => {
        const periodMap = new Map();
        const groupNames = new Map();
        const unitSet = new Set();

        series.forEach(item => {
            const rawPeriod = item?.periodo || '-';
            const period = appliedFilters.granularity === 'anual' && rawPeriod !== '-'
                ? rawPeriod.split('-')[0]
                : rawPeriod;
            const groupId = String(item?.jerarquia_id ?? item?.jerarquia_nombre ?? 'sin-grupo');
            const groupName = item?.jerarquia_nombre || 'Sin sitio';
            const costo = Number(item?.costo) || 0;
            const consumo = item?.consumo === null || item?.consumo === undefined ? null : Number(item?.consumo);
            const facturasCount = Number(item?.facturas_count) || 0;
            const unit = item?.unidad || '';

            groupNames.set(groupId, groupName);
            if (unit) {
                unitSet.add(unit);
            }

            if (!periodMap.has(period)) {
                periodMap.set(period, {
                    period,
                    groups: {},
                    totalCost: 0,
                    totalConsumption: 0,
                    facturasCount: 0,
                    units: new Set(),
                });
            }

            const currentPeriod = periodMap.get(period);
            if (unit) {
                currentPeriod.units.add(unit);
            }

            if (!currentPeriod.groups[groupId]) {
                currentPeriod.groups[groupId] = {
                    groupId,
                    groupName,
                    costo: 0,
                    consumo: 0,
                    facturasCount: 0,
                    units: new Set(),
                };
            }

            currentPeriod.groups[groupId].costo += costo;
            currentPeriod.groups[groupId].facturasCount += facturasCount;
            if (unit) {
                currentPeriod.groups[groupId].units.add(unit);
            }
            currentPeriod.totalCost += costo;
            currentPeriod.facturasCount += facturasCount;

            if (consumo !== null) {
                currentPeriod.groups[groupId].consumo += consumo;
                currentPeriod.totalConsumption += consumo;
            }
        });

        const orderedGroups = Array.from(groupNames.entries())
            .map(([id, name], index) => ({
                id,
                name,
                color: GROUP_COLORS[index % GROUP_COLORS.length],
            }))
            .sort((firstItem, secondItem) => firstItem.name.localeCompare(secondItem.name));

        const rows = Array.from(periodMap.values())
            .sort((firstItem, secondItem) => comparePeriodStrings(firstItem.period, secondItem.period))
            .map(row => ({
                ...row,
                label: getPeriodLabel(row.period, appliedFilters.granularity),
                segments: orderedGroups.map(group => ({
                    ...group,
                    costo: row.groups[group.id]?.costo || 0,
                    consumo: row.groups[group.id]?.consumo || 0,
                    facturasCount: row.groups[group.id]?.facturasCount || 0,
                    unitLabel: Array.from(row.groups[group.id]?.units || []).join(' / '),
                })),
            }));

        const totalCost = rows.reduce((acc, r) => acc + r.totalCost, 0);
        const totalConsumption = rows.reduce((acc, r) => acc + r.totalConsumption, 0);
        const totalInvoices = rows.reduce((acc, r) => acc + r.facturasCount, 0);

        const allPeriods = generatePeriodRange(appliedFilters.periodFrom, appliedFilters.periodTo, appliedFilters.granularity);
        const existingPeriodSet = new Set(rows.map(r => r.period));
        const filledRows = [...rows];

        allPeriods.forEach(period => {
            if (!existingPeriodSet.has(period)) {
                filledRows.push({
                    period,
                    label: getPeriodLabel(period, appliedFilters.granularity),
                    groups: {},
                    totalCost: 0,
                    totalConsumption: 0,
                    facturasCount: 0,
                    units: new Set(),
                    segments: orderedGroups.map(group => ({
                        ...group,
                        costo: 0,
                        consumo: 0,
                        facturasCount: 0,
                        unitLabel: '',
                    })),
                });
            }
        });

        filledRows.sort((a, b) => comparePeriodStrings(a.period, b.period));

        return {
            rows: filledRows,
            hasData: rows.length > 0,
            groups: orderedGroups,
            units: Array.from(unitSet),
            summary: {
                totalCost,
                totalConsumption,
                periods: rows.length,
                totalInvoices,
            },
        };
    }, [appliedFilters.granularity, appliedFilters.periodFrom, appliedFilters.periodTo, series]);

    const handleGranularityChange = nextGranularity => {
        const nextPeriod = buildDefaultPeriod(nextGranularity);

        setFilters(previousState => ({
            ...previousState,
            granularity: nextGranularity,
            periodFrom: nextPeriod.from,
            periodTo: nextPeriod.to,
        }));
    };

    const handleFieldChange = event => {
        const { name, value } = event.target;
        setFilters(previousState => ({
            ...previousState,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({
            ...filters,
            siteIds: [...filters.siteIds],
        });
        setFiltersOpen(false);
    };

    const handleResetFilters = () => {
        const initialFilters = buildInitialFilters();
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
        setFiltersOpen(false);
    };

    const renderPeriodFields = () => {
        if (filters.granularity === 'anual') {
            const currentYear = new Date().getFullYear();
            const yearOptions = Array.from({ length: 11 }, (_, index) => String(currentYear - 7 + index));

            return (
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField select label='Desde' name='periodFrom' value={filters.periodFrom} onChange={handleFieldChange} variant='outlined' className={classes.field}>
                            {yearOptions.map(year => (
                                <MenuItem key={year} value={year}>
                                    {year}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField select label='Hasta' name='periodTo' value={filters.periodTo} onChange={handleFieldChange} variant='outlined' className={classes.field}>
                            {yearOptions.map(year => (
                                <MenuItem key={year} value={year}>
                                    {year}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <TextField
                        label='Desde'
                        name='periodFrom'
                        type={filters.granularity === 'mensual' ? 'month' : 'date'}
                        value={filters.periodFrom}
                        onChange={handleFieldChange}
                        variant='outlined'
                        className={classes.field}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label='Hasta'
                        name='periodTo'
                        type={filters.granularity === 'mensual' ? 'month' : 'date'}
                        value={filters.periodTo}
                        onChange={handleFieldChange}
                        variant='outlined'
                        className={classes.field}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <TopNav titulo='Facturación' subtitulo='| Consumos históricos' />
            <Container>
                <Box className={classes.root}>
                    <Paper className={classes.hero}>
                        <Box className={classes.heroRow}>
                            <Box>
                                <Box className={classes.titleWrap}>
                                    <Box className={classes.titleIcon}>
                                        <BarChartOutlinedIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant='h6' className={classes.pageTitle}>
                                            Consumos
                                        </Typography>
                                        <Typography className={classes.pageSubtitle}>
                                            Visualizá costos y consumos energéticos por período. Los datos se cargan automáticamente con la configuración activa — usá el botón Filtrar para cambiar el período, el servicio o el proveedor.
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* <Typography className={classes.helperText}>
                                    Los valores se muestran prorrateados según el período real de cada factura cargada.
                                </Typography> */}
                            </Box>

                            <Box className={classes.headerActions}>
                                <Button variant='contained' color='primary' startIcon={<FilterListRoundedIcon />} onClick={() => setFiltersOpen(true)}>
                                    Filtrar
                                </Button>
                            </Box>
                        </Box>

                        <Box className={classes.chipsRow}>
                            <Chip
                                size='small'
                                label={GRANULARITY_OPTIONS.find(o => o.value === appliedFilters.granularity)?.label}
                                className={classes.chip}
                                onClick={() => setFiltersOpen(true)}
                            />
                            <Chip
                                size='small'
                                label={`${getPeriodLabel(appliedFilters.periodFrom, appliedFilters.granularity)} – ${getPeriodLabel(appliedFilters.periodTo, appliedFilters.granularity)}`}
                                className={classes.chip}
                                onClick={() => setFiltersOpen(true)}
                            />
                            {appliedFilters.serviceType ? (
                                <Chip
                                    size='small'
                                    label={SERVICE_OPTIONS.find(o => o.value === appliedFilters.serviceType)?.label}
                                    className={classes.chip}
                                    onClick={() => setFiltersOpen(true)}
                                />
                            ) : (
                                <Chip
                                    size='small'
                                    label='Todos los servicios'
                                    className={classes.chip}
                                    onClick={() => setFiltersOpen(true)}
                                />
                            )}
                        </Box>
                    </Paper>

                    {pageError && (
                        <Alert severity='warning' className={classes.inlineAlert}>
                            {pageError}
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box className={classes.mainColumn}>
                                {!loadingData && !loadingMetadata && groupedData.hasData && (
                                    <Grid container spacing={2} className={classes.summaryGrid}>
                                        <Grid item xs={12} sm={6} xl={3}>
                                            <Paper className={classes.summaryCard}>
                                                <Typography className={classes.summaryLabel}>Costo total prorrateado</Typography>
                                                <Typography className={classes.summaryValue}>{formatCurrency(groupedData.summary.totalCost)}</Typography>
                                                <Typography className={classes.summaryHint}>Suma de costos distribuidos sobre el período consultado.</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6} xl={3}>
                                            <Paper className={classes.summaryCard}>
                                                <Typography className={classes.summaryLabel}>Consumo total</Typography>
                                                <Typography className={classes.summaryValue}>
                                                    {groupedData.summary.totalConsumption ? formatNumber(groupedData.summary.totalConsumption, 1) : 'Sin dato'}
                                                </Typography>
                                                <Typography className={classes.summaryHint}>
                                                    {groupedData.units.length ? `Unidad: ${groupedData.units.join(' / ')}` : 'Algunas facturas pueden no incluir consumo.'}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6} xl={3}>
                                            <Paper className={classes.summaryCard}>
                                                <Typography className={classes.summaryLabel}>Períodos con datos</Typography>
                                                <Typography className={classes.summaryValue}>{integerFormatter.format(groupedData.summary.periods)}</Typography>
                                                <Typography className={classes.summaryHint}>Cantidad de períodos con al menos una factura prorrateada.</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6} xl={3}>
                                            <Paper className={classes.summaryCard}>
                                                <Typography className={classes.summaryLabel}>Facturas involucradas</Typography>
                                                <Typography className={classes.summaryValue}>{integerFormatter.format(groupedData.summary.totalInvoices)}</Typography>
                                                <Typography className={classes.summaryHint}>Total de facturas que aportan al resultado según el filtro activo.</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                )}

                                <Paper className={classes.card}>
                                    <Box className={classes.cardHeader} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                                        <Box>
                                            <Typography className={classes.sectionTitle}>
                                                {chartMetric === 'cost' ? 'Visualización de costos prorrateados' : 'Visualización de consumos'}
                                            </Typography>
                                            <Typography className={classes.sectionCaption}>
                                                {chartMetric === 'cost'
                                                    ? 'Cada barra representa un período calendario, mostrando los costos prorrateados de las facturas involucradas.'
                                                    : 'Cada barra representa un período calendario, mostrando el consumo registrado en las facturas involucradas.'}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.metricToggle}>
                                            <Button
                                                className={clsx(classes.metricToggleBtn, { [classes.metricToggleBtnActive]: chartMetric === 'cost' })}
                                                onClick={() => setChartMetric('cost')}
                                            >
                                                Costo
                                            </Button>
                                            <Button
                                                className={clsx(classes.metricToggleBtn, { [classes.metricToggleBtnActive]: chartMetric === 'consumption' })}
                                                onClick={() => setChartMetric('consumption')}
                                            >
                                                Consumo
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Box className={classes.chartCardBody}>
                                        {loadingData || loadingMetadata ? (
                                            <Box className={classes.loadingBox}>
                                                <CircularProgress size={30} />
                                            </Box>
                                        ) : !groupedData.hasData ? (
                                            <Box className={classes.emptyState}>
                                                <Typography className={classes.emptyStateTitle}>Sin datos para el filtro activo</Typography>
                                                <Typography className={classes.emptyStateText}>
                                                    No encontramos facturas de{' '}
                                                    <strong>{SERVICE_OPTIONS.find(o => o.value === appliedFilters.serviceType)?.label?.toLowerCase() || 'ningún servicio'}</strong>
                                                    {' '}entre{' '}
                                                    <strong>{getPeriodLabel(appliedFilters.periodFrom, appliedFilters.granularity)}</strong>
                                                    {' '}y{' '}
                                                    <strong>{getPeriodLabel(appliedFilters.periodTo, appliedFilters.granularity)}</strong>.
                                                    {' '}Probá ampliando el rango o cambiando el servicio.
                                                </Typography>
                                                <Box style={{ marginTop: 16 }}>
                                                    <Button
                                                        variant='contained'
                                                        color='primary'
                                                        size='small'
                                                        startIcon={<FilterListRoundedIcon />}
                                                        onClick={() => setFiltersOpen(true)}
                                                    >
                                                        Cambiar filtros
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box className={classes.chartShell}>
                                                <ConsumptionHistoryChart rows={groupedData.rows} groups={groupedData.groups} metric={chartMetric} unit={groupedData.units[0] || ''} />
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>

                                {!loadingData && !loadingMetadata && groupedData.hasData && (
                                <Paper className={classes.card}>
                                    <Box className={classes.cardHeader}>
                                        <Typography className={classes.sectionTitle}>Resultados prorrateados</Typography>
                                        <Typography className={classes.sectionCaption}>
                                            Tabla comparativa por período calendario, con columnas dinámicas por sitio.
                                        </Typography>
                                    </Box>

                                    <Box className={classes.tableCardBody}>
                                        <TableContainer>
                                            <Table>
                                                <TableHead className={classes.tableHead}>
                                                    <TableRow>
                                                        <TableCell className={`${classes.headCell} ${classes.stickyFirstHeadCell}`}>Período</TableCell>
                                                        {groupedData.groups.map(group => (
                                                            <TableCell key={group.id} className={classes.headCell} align='right'>
                                                                <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                                                    <Box style={{ width: 8, height: 8, borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                                                                    {group.name}
                                                                </Box>
                                                            </TableCell>
                                                        ))}
                                                        <TableCell className={classes.headCell} align='right'>Total ($)</TableCell>
                                                        <TableCell className={classes.headCell} align='right'>
                                                            {groupedData.units.length ? `Consumo (${groupedData.units.join(' / ')})` : 'Consumo'}
                                                        </TableCell>
                                                        <TableCell className={classes.headCell} align='right'>Facturas</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {groupedData.rows.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={groupedData.groups.length + 4} className={classes.bodyCell} style={{ textAlign: 'center', padding: '24px 16px' }}>
                                                                Sin resultados — ajustá los filtros para ver datos en esta tabla.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        groupedData.rows.map(row => (
                                                            <TableRow key={row.period} hover>
                                                                <TableCell className={`${classes.bodyCell} ${classes.stickyFirstCell}`}>
                                                                    {row.label}
                                                                </TableCell>
                                                                {row.segments.map(segment => (
                                                                    <TableCell key={`${row.period}-${segment.id}`} className={classes.bodyCell} align='right'>
                                                                        {segment.costo ? formatCurrency(segment.costo) : '-'}
                                                                    </TableCell>
                                                                ))}
                                                                <TableCell className={classes.bodyCell} align='right'>{formatCurrency(row.totalCost)}</TableCell>
                                                                <TableCell className={classes.bodyCell} align='right'>
                                                                    {row.totalConsumption ? `${formatNumber(row.totalConsumption, 2)} ${Array.from(row.units).join(' / ')}`.trim() : '-'}
                                                                </TableCell>
                                                                <TableCell className={classes.bodyCell} align='right'>{integerFormatter.format(row.facturasCount)}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Paper>
                                )}

                                {/* {gaps.length > 0 && (
                                    <Alert severity='info'>
                                        Se detectaron {gaps.length} períodos sin factura esperada para la configuración activa. El backend los marca como faltantes para ayudarte a identificar huecos de carga.
                                    </Alert>
                                )} */}
                            </Box>
                        </Grid>
                    </Grid>

                    <Drawer
                        anchor='right'
                        open={filtersOpen}
                        onClose={() => setFiltersOpen(false)}
                        classes={{ paper: classes.filterDrawerPaper }}
                    >
                        <Box className={classes.drawerHeader}>
                            <Box className={classes.drawerHeaderText}>
                                <Typography className={classes.drawerTitle}>Filtrar</Typography>
                                <Typography className={classes.drawerSubtitle}>
                                    Elegí granularidad, período, servicio y proveedor (opcional).
                                </Typography>
                            </Box>
                            <IconButton size='small' onClick={() => setFiltersOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Box className={classes.drawerContent}>
                            <Paper className={`${classes.card} ${classes.filtersCard}`}>
                                <Box className={classes.filtersBody}>
                                    <Box>
                                        <Typography className={classes.summaryLabel}>Granularidad</Typography>
                                        <Box className={classes.segmentedControl}>
                                            {GRANULARITY_OPTIONS.map(option => (
                                                <Button
                                                    key={option.value}
                                                    onClick={() => handleGranularityChange(option.value)}
                                                    className={clsx(classes.segmentButton, {
                                                        [classes.segmentButtonActive]: filters.granularity === option.value,
                                                    })}
                                                >
                                                    {option.label}
                                                </Button>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography className={classes.summaryLabel}>Período</Typography>
                                        {renderPeriodFields()}
                                    </Box>

                                    <TextField select label='Tipo de servicio' name='serviceType' value={filters.serviceType} onChange={handleFieldChange} variant='outlined' className={classes.field}>
                                        {SERVICE_OPTIONS.map(option => (
                                            <MenuItem key={option.value || 'all'} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField select label='Proveedor' name='providerId' value={filters.providerId} onChange={handleFieldChange} variant='outlined' className={classes.field}>
                                        <MenuItem value=''>Todos los proveedores</MenuItem>
                                        {providers.map(provider => (
                                            <MenuItem key={provider.id} value={provider.id}>
                                                {provider.name}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    {/* <FormControl variant='outlined' className={classes.field}>
                                        <Select
                                            multiple
                                            displayEmpty
                                            value={filters.siteIds}
                                            onChange={handleSitesChange}
                                            renderValue={selected => {
                                                if (!selected.length) {
                                                    return 'Todos los sitios';
                                                }

                                                return selected
                                                    .map(siteId => locationNameById[siteId] || siteId)
                                                    .slice(0, 2)
                                                    .join(', ')
                                                    + (selected.length > 2 ? ` +${selected.length - 2}` : '');
                                            }}
                                        >
                                            {locations.map(location => (
                                                <MenuItem key={location.id} value={location.id}>
                                                    {location.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl> */}

                                    <Box className={classes.filterActions}>
                                        <Button className={classes.secondaryButton} onClick={handleResetFilters}>
                                            Limpiar
                                        </Button>
                                        <Button className={classes.primaryButton} onClick={handleApplyFilters}>
                                            Aplicar
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </Drawer>
                </Box>
            </Container>
        </>
    );
}

export default ConsumptionHistory;
